export type SupportErrorAlertTriageStatus =
  | "untriaged"
  | "known"
  | "escalated"
  | "resolved";

export interface SupportErrorAlertRecord {
  id: string;
  user: string;
  userEmail: string;
  createdAt: string | null;
  triageStatus: SupportErrorAlertTriageStatus;
  sentToSlack: string | null;
  isVisibleToUser: boolean;
  message: string;
  errorMessage: string;
  stack: string;
  platform: string;
  integrationId: string | null;
  type: string | null;
  errorCode: string | null;
  formSubject: string | null;
  formDescription: string | null;
  extraFields: Record<string, unknown>;
}

export interface SupportErrorAlertSearchFilter {
  field: string;
  value: string;
}

export interface SupportErrorAlertTimelineBucket {
  start: string;
  end: string;
  count: number;
}

export interface SupportErrorAlertTimeline {
  bucketMs: number;
  buckets: SupportErrorAlertTimelineBucket[];
}

export interface SupportErrorAlertsPage {
  alerts: SupportErrorAlertRecord[];
  nextCursor: string | null;
  timeline?: SupportErrorAlertTimeline;
}

export type ErrorAlertSearchFieldId =
  | "user"
  | "uid"
  | "platform"
  | "type"
  | "integration"
  | "error"
  | "message"
  | "stack"
  | "new";

export interface ErrorAlertFilterOption {
  value: string;
  label: string;
}

export interface ErrorAlertSearchField {
  id: ErrorAlertSearchFieldId;
  label: string;
  valueOptions?: ErrorAlertFilterOption[];
}

export const ERROR_ALERT_PLATFORMS: ErrorAlertFilterOption[] = [
  { value: "eBay", label: "eBay" },
  { value: "Etsy", label: "Etsy" },
  { value: "Amazon", label: "Amazon" },
  { value: "Mercari", label: "Mercari" },
  { value: "Whatnot", label: "Whatnot" },
  { value: "Vinted", label: "Vinted" },
  { value: "Depop", label: "Depop" },
  { value: "Poshmark", label: "Poshmark" },
  { value: "Plaid", label: "Plaid" },
];

export const ERROR_ALERT_TYPES: ErrorAlertFilterOption[] = [
  {
    value: "general-error-notification-form-submission",
    label: "General error (user notification)",
  },
  { value: "ebay-rate-limit", label: "eBay rate limit" },
  { value: "etsy-no-shop-error", label: "Etsy no shop" },
  {
    value: "receipt-validation-error",
    label: "Etsy receipt validation warning",
  },
  {
    value: "refund-estimation-warning",
    label: "Etsy refund estimation warning",
  },
  { value: "amazonInventory", label: "Amazon inventory" },
];

/** Internal slug stored in Firestore `error` when backend categorizes a failure. */
export const ERROR_ALERT_ERROR_IDENTIFIERS: ErrorAlertFilterOption[] = [
  {
    value: "depop-items-amount-mismatch",
    label: "Depop item amount mismatch",
  },
  {
    value: "depop-possible-partial-refund",
    label: "Depop possible partial refund",
  },
  {
    value: "vinted-sale-price-mismatch",
    label: "Vinted sale price mismatch",
  },
];

export const ERROR_ALERT_NEW_OPTIONS: ErrorAlertFilterOption[] = [
  { value: "true", label: "New (visible to user)" },
  { value: "false", label: "Not new (hidden from user)" },
];

export const ERROR_ALERT_SEARCH_FIELDS: ErrorAlertSearchField[] = [
  { id: "user", label: "User email" },
  { id: "uid", label: "User UID" },
  {
    id: "platform",
    label: "Platform",
    valueOptions: ERROR_ALERT_PLATFORMS,
  },
  {
    id: "type",
    label: "Alert type",
    valueOptions: ERROR_ALERT_TYPES,
  },
  { id: "integration", label: "Integration" },
  {
    id: "error",
    label: "Error identifier",
    valueOptions: ERROR_ALERT_ERROR_IDENTIFIERS,
  },
  { id: "message", label: "Message" },
  { id: "stack", label: "Stack trace" },
  {
    id: "new",
    label: "New",
    valueOptions: ERROR_ALERT_NEW_OPTIONS,
  },
];

export function getErrorAlertFilterValueLabel(
  field: ErrorAlertSearchFieldId,
  value: string
) {
  const fieldConfig = ERROR_ALERT_SEARCH_FIELDS.find(
    (entry) => entry.id === field
  );
  const option = fieldConfig?.valueOptions?.find(
    (entry) => entry.value === value
  );
  return option?.label || value;
}
